import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  User,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  Ban,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useImportHistory, useRegeocodeVoters, useGeocodingStats } from '@/hooks/queries';
import type { ImportJobHistoryDto } from '@signalvote/shared';
import { useImportProgressStore } from '@/stores/importProgressStore';

interface ImportHistoryListProps {
  onReviewErrors: (job: ImportJobHistoryDto) => void;
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string; icon?: React.ReactNode }> = {
    Completed: {
      label: 'Completed',
      className: 'bg-success/10 text-success',
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
    Failed: {
      label: 'Failed',
      className: 'bg-destructive/10 text-destructive',
      icon: <XCircle className="h-3 w-3" />,
    },
    Running: {
      label: 'Running',
      className: 'bg-info/10 text-info',
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    Cancelled: {
      label: 'Cancelled',
      className: 'bg-warning/10 text-warning',
      icon: <Ban className="h-3 w-3" />,
    },
    Pending: {
      label: 'Pending',
      className: 'bg-muted text-muted-foreground',
      icon: <Clock className="h-3 w-3" />,
    },
  };

  const c = config[status] ?? config.Pending;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        c.className
      )}
    >
      {c.icon}
      {c.label}
    </span>
  );
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ImportHistoryList({ onReviewErrors }: ImportHistoryListProps) {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  const { data: history, isLoading } = useImportHistory({ page, pageSize: 5 });
  const { mutate: regeocodeVoters, isPending: isRegeocoding } = useRegeocodeVoters();
  const { data: geoStats } = useGeocodingStats();
  const activeProgress = useImportProgressStore((s) => s.progress);
  const hasActiveJob = activeProgress != null &&
    (activeProgress.status === 'Pending' || activeProgress.status === 'Running');

  const jobs = history?.items ?? [];
  const totalPages = history ? Math.ceil(history.totalCount / 5) : 1;

  return (
    <div className="border border-border rounded-lg">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className={cn(
          'flex items-center justify-between w-full px-4 py-3 text-sm font-medium',
          'text-foreground hover:bg-muted/50 transition-colors rounded-lg',
          expanded && 'rounded-b-none border-b border-border'
        )}
      >
        <span className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          Import History
          {history?.totalCount != null && history.totalCount > 0 && (
            <span className="text-xs text-muted-foreground font-normal">
              ({history.totalCount})
            </span>
          )}
        </span>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4">
          {isLoading && (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              <span className="text-sm">Loading history...</span>
            </div>
          )}

          {!isLoading && jobs.length === 0 && (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No imports yet.
            </p>
          )}

          {/* Standalone re-geocode action */}
          {!isLoading && jobs.length > 0 && geoStats && (
            <div className="bg-muted/30 rounded-md px-3 py-2.5 mt-2 space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 inline mr-1" />
                  {geoStats.needsGeocoding > 0 ? (
                    <>
                      <span className="font-medium text-foreground">
                        {geoStats.needsGeocoding.toLocaleString()}
                      </span>
                      {' of '}
                      {geoStats.totalVoters.toLocaleString()}
                      {' voters need re-geocoding'}
                    </>
                  ) : (
                    <>All {geoStats.totalVoters.toLocaleString()} voters have high-accuracy coordinates</>
                  )}
                </div>
                {geoStats.needsGeocoding > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => regeocodeVoters()}
                    disabled={isRegeocoding || hasActiveJob}
                    className="text-xs shrink-0 ml-3"
                  >
                    {isRegeocoding ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        Starting...
                      </>
                    ) : hasActiveJob ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        Job in progress...
                      </>
                    ) : (
                      <>
                        <MapPin className="h-3.5 w-3.5 mr-1" />
                        Re-geocode ({geoStats.needsGeocoding.toLocaleString()})
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}

          {!isLoading && jobs.length > 0 && (
            <div className="space-y-1 mt-2">
              {jobs.map((job) => {
                const isExpanded = expandedJobId === job.id;
                const hasErrors = job.errorCount > 0;
                const canReviewErrors =
                  hasErrors && (job.status === 'Completed' || job.status === 'Cancelled');

                return (
                  <div key={job.id} className="rounded-md border border-border overflow-hidden">
                    {/* Job row */}
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedJobId((prev) => (prev === job.id ? null : job.id))
                      }
                      className={cn(
                        'flex flex-col w-full px-3 py-2.5 text-left',
                        'hover:bg-muted/50 transition-colors',
                        isExpanded && 'bg-muted/30'
                      )}
                    >
                      {/* Top line: date + status + duration */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {formatDate(job.startedAt)}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          <StatusBadge status={job.status} />
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDuration(job.durationSeconds)}
                          </span>
                        </div>
                      </div>

                      {/* Bottom line: who imported + stats */}
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {job.startedByName ?? 'Unknown'}
                        </span>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-success">
                            {job.importedCount} added
                          </span>
                          {(job.updatedCount ?? 0) > 0 && (
                            <span className="text-info">
                              {job.updatedCount} updated
                            </span>
                          )}
                          {(job.linkedCount ?? 0) > 0 && (
                            <span className="text-info">
                              {job.linkedCount} linked
                            </span>
                          )}
                          {job.skippedCount > 0 && (
                            <span className="text-muted-foreground">
                              {job.skippedCount} duplicates
                            </span>
                          )}
                          {hasErrors && (
                            <span className="text-destructive flex items-center gap-0.5">
                              <AlertCircle className="h-3 w-3" />
                              {job.errorCount} errors
                            </span>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expanded error details */}
                    {isExpanded && (
                      <div className="border-t border-border bg-muted/20 px-3 py-2.5 space-y-2">
                        {/* Accuracy breakdown */}
                        {job.accuracyBreakdown && Object.keys(job.accuracyBreakdown).length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-foreground">Geocoding Accuracy</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                              {Object.entries(job.accuracyBreakdown)
                                .sort(([, a], [, b]) => b - a)
                                .map(([level, count]) => (
                                  <div key={level} className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">{level}</span>
                                    <span className="font-medium tabular-nums">{count.toLocaleString()}</span>
                                  </div>
                                ))}
                            </div>
                            {(() => {
                              const lowAccuracyCount = Object.entries(job.accuracyBreakdown)
                                .filter(([level]) => level !== 'GeoMatchRooftop' && level !== 'BuildingCentroid' && level !== 'GoogleApi')
                                .reduce((sum, [, count]) => sum + count, 0);
                              if (lowAccuracyCount > 0 && job.status === 'Completed') {
                                return (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      regeocodeVoters();
                                    }}
                                    disabled={isRegeocoding || hasActiveJob}
                                    className="text-xs mt-1"
                                  >
                                    <MapPin className="h-3.5 w-3.5 mr-1" />
                                    {isRegeocoding || hasActiveJob ? 'Job in progress...' : `Re-geocode Low Accuracy Voters (${lowAccuracyCount.toLocaleString()})`}
                                  </Button>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}

                        {job.errors && job.errors.length > 0 ? (
                          <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                            {job.errors.slice(0, 20).map((err, i) => (
                              <li
                                key={i}
                                className="text-xs text-destructive flex items-start gap-1.5"
                              >
                                <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                                <span>{err}</span>
                              </li>
                            ))}
                            {job.errors.length > 20 && (
                              <li className="text-xs text-muted-foreground italic">
                                ...and {job.errors.length - 20} more errors
                              </li>
                            )}
                          </ul>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            {hasErrors
                              ? 'Error details not available in summary. Click "Review Errors" for full details.'
                              : 'No errors recorded for this import.'}
                          </p>
                        )}

                        {canReviewErrors && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              onReviewErrors(job);
                            }}
                            className="text-xs"
                          >
                            <AlertCircle className="h-3.5 w-3.5 mr-1" />
                            Review Errors
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="text-xs"
              >
                Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="text-xs"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
